import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import React, { useEffect, useRef, useState } from 'react';
import { ReactComponent as ArrowDownIcon } from '../../../../assets/icons/arrow-down-s-line.svg';
import { ReactComponent as CloseCircleGreyIcon } from '../../../../assets/icons/close-circle-fill-grey.svg';
import { getFlagSvg } from '../../../../Components/Common/Flag/Flag';
import RicInputBox from '../../../../Components/Common/RicInputBox/RicInputBox';
import '../../../../Components/Common/typography.css';
import {
  COMPARISON_COLORS,
  MAX_COMPARISON,
  MOCK_INDEXES,
} from '../../constants';
import { ComparisonItem } from '../../types';
import ColorSwatch from '../common/ColorSwatch';
import SelectDropdown from '../common/SelectDropdown';
import SettingsCheckbox from '../common/SettingsCheckbox';
import SettingsPopup from '../common/SettingsPopup';
import './ComparisonSettings.css';

interface ComparisonSettingsProps {
  comparisonMode: boolean;
  comparisonList: ComparisonItem[];
  onToggleMode: (enabled: boolean) => void;
  onAdd: (item: ComparisonItem) => void;
  onRemove: (id: string) => void;
  onUpdate: (id: string, changes: Partial<ComparisonItem>) => void;
  token?: string;
}

const ComparisonSettings: React.FC<ComparisonSettingsProps> = ({
  comparisonMode,
  comparisonList,
  onToggleMode,
  onAdd,
  onRemove,
  onUpdate,
  token = '',
}) => {
  const [assetType, setAssetType] = useState<'index' | 'stock'>('index');
  const [selectedInstrument, setSelectedInstrument] = useState('');
  const [isIndexPickerOpen, setIsIndexPickerOpen] = useState(false);
  const indexPickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isIndexPickerOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        indexPickerRef.current &&
        !indexPickerRef.current.contains(e.target as Node)
      ) {
        setIsIndexPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isIndexPickerOpen]);

  const atMax = comparisonList.length >= MAX_COMPARISON;
  const existingIds = comparisonList.map((c) => c.symbol);

  const availableIndexPool = MOCK_INDEXES.filter(
    (item) => !existingIds.includes(item.ric),
  );

  const handleAddIndex = () => {
    if (atMax || !selectedInstrument) return;
    const item = MOCK_INDEXES.find((p) => p.ric === selectedInstrument);
    if (!item || existingIds.includes(item.ric)) return;
    const colorIdx = comparisonList.length % COMPARISON_COLORS.length;
    onAdd({
      id: `${item.ric}-${Date.now()}`,
      symbol: item.ric,
      displaySymbol: item.symbol,
      name: item.name,
      assetType: 'index',
      color: COMPARISON_COLORS[colorIdx],
      thickness: 2,
      style: 's',
      countryCode: item.countryCode,
    });
    setSelectedInstrument('');
  };

  const selectedIndexItem = MOCK_INDEXES.find(
    (i) => i.ric === selectedInstrument,
  );

  const handleSelectStock = (option: any) => {
    const symbol: string = option.symbol ?? '';
    const name: string = option.name ?? '';
    if (atMax || !symbol || existingIds.includes(symbol)) return;
    const colorIdx = comparisonList.length % COMPARISON_COLORS.length;
    onAdd({
      id: `${symbol}-${Date.now()}`,
      symbol,
      name,
      assetType: 'stock',
      color: COMPARISON_COLORS[colorIdx],
      thickness: 2,
      style: 's',
      countryCode: option.countryCode,
    });
  };

  return (
    <SettingsPopup width={370} className="comparison-popup">
      {/* Enter compare mode */}
      <div
        className="comparison-mode-row"
        onClick={() => onToggleMode(!comparisonMode)}
      >
        <SettingsCheckbox
          checked={comparisonMode}
          onChange={() => onToggleMode(!comparisonMode)}
        />
        <span className="body-m-regular">Enter compare mode</span>
      </div>

      {comparisonMode && (
        <>
          {/* Selected comparison items */}
          <div className="comparison-item-list">
            {comparisonList.map((item) => (
              <div key={item.id} className="comparison-item-row">
                <IconButton
                  size="medium"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(item.id);
                  }}
                  aria-label={`Remove ${item.symbol}`}
                  sx={{ padding: 0, width: 20, height: 20 }}
                >
                  <CloseCircleGreyIcon style={{ width: 20, height: 20 }} />
                </IconButton>
                {item.countryCode && (
                  <span className="comparison-item-flag">
                    {getFlagSvg(item.countryCode)}
                  </span>
                )}
                <span className="body-m-regular comparison-item-symbol">
                  {item.displaySymbol ?? item.symbol}
                </span>
                <ColorSwatch
                  color={item.color}
                  onChange={(color) => onUpdate(item.id, { color })}
                  size={24}
                />
                <SelectDropdown
                  type="thickstyle"
                  value={item.thickness}
                  onChange={(v) => onUpdate(item.id, { thickness: Number(v) })}
                />
                <SelectDropdown
                  type="linestyle"
                  value={item.style}
                  onChange={(v) => onUpdate(item.id, { style: v as 's' | 'd' })}
                />
              </div>
            ))}
          </div>

          {/* Add instrument section */}
          {!atMax && (
            <div className="comparison-add-section">
              <span className="body-m-regular comparison-add-title">
                Add instrument
              </span>

              <div className="comparison-add-row">
                <SelectDropdown
                  options={[
                    { label: 'Index', value: 'index' },
                    { label: 'Stock', value: 'stock' },
                  ]}
                  value={assetType}
                  onChange={(v) => {
                    setAssetType(v as 'index' | 'stock');
                    setSelectedInstrument('');
                    setIsIndexPickerOpen(false);
                  }}
                  className="comparison-add-type"
                />

                {assetType === 'index' ? (
                  <>
                    {/* Custom index picker: shows flag + symbol in trigger and options */}
                    <div className="index-picker" ref={indexPickerRef}>
                      <button
                        className="index-picker-trigger body-m-regular"
                        type="button"
                        onClick={() => setIsIndexPickerOpen((v) => !v)}
                      >
                        {selectedIndexItem ? (
                          <>
                            <span className="comparison-item-flag">
                              {getFlagSvg(selectedIndexItem.countryCode)}
                            </span>
                            <span className="index-picker-selected-symbol">
                              {selectedIndexItem.symbol}
                            </span>
                          </>
                        ) : (
                          <span className="index-picker-placeholder">
                            Select index...
                          </span>
                        )}
                        <ArrowDownIcon className="index-picker-arrow" />
                      </button>
                      {isIndexPickerOpen && (
                        <div
                          className="index-picker-menu"
                          data-outside-ignore="true"
                        >
                          {availableIndexPool.map((item) => (
                            <div
                              key={item.ric}
                              className={`index-picker-item body-m-regular${selectedInstrument === item.ric ? ' index-picker-item--active' : ''}`}
                              onClick={() => {
                                setSelectedInstrument(item.ric);
                                setIsIndexPickerOpen(false);
                              }}
                            >
                              <span className="comparison-item-flag">
                                {getFlagSvg(item.countryCode)}
                              </span>
                              <span className="index-picker-item-symbol">
                                {item.symbol}
                              </span>
                              <span className="index-picker-item-name">
                                {item.name}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddIndex();
                      }}
                      disabled={!selectedInstrument}
                      sx={{
                        backgroundColor: 'var(--color-brand, #d44500)',
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: '14px',
                        textTransform: 'none',
                        padding: '6px 16px',
                        whiteSpace: 'nowrap',
                        boxShadow: 'none',
                        '&:hover': {
                          backgroundColor: 'var(--color-brand, #d44500)',
                          opacity: 0.9,
                          boxShadow: 'none',
                        },
                        '&.Mui-disabled': {
                          opacity: 0.5,
                          backgroundColor: 'var(--color-brand, #d44500)',
                          color: '#fff',
                        },
                      }}
                    >
                      Add
                    </Button>
                  </>
                ) : (
                  <div className="comparison-stock-search comparison-stock-search-input-wrap">
                    <RicInputBox
                      placeholder="Search by Ticker or Keyword"
                      onSelectOption={handleSelectStock}
                      token={token}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {atMax && (
            <div className="comparison-limit-message body-s-regular">
              Maximum {MAX_COMPARISON} instruments reached
            </div>
          )}
        </>
      )}
    </SettingsPopup>
  );
};

export default ComparisonSettings;
